import {
  AppBar,
  Box,
  Button,
  findBy,
  Icon,
  makeStyles,
  MuiStack,
  Toolbar,
} from '@perk-ui/core'
import clsx from 'clsx'
import { LogOut } from 'react-feather'
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'

import brandLogo from '../../assets/BrandLogo.png'
import { useSitesContext } from '../../components/SitesProvider'
import { manufacturerLogo, organizationLogo } from '../../config/constants'
import { useAuth } from '../../features/auth/AuthContext'
import usePatientCases from '../../features/query-hooks/usePatientCases'
import AddCase from './AddCase/AddCase'
import Diagnosis from './Diagnosis/Diagnosis'
import Patient from './Patient/Patient'
import PatientDetails from './PatientDetails/PatientDetails'
import PatientSearch from './PatientSearch/PatientSearch'
import PatientSelfOnboardUrls, {
  PatientSelfOnboardParams,
} from './PatientSelfOnboardUrls'
import Survey from './Survey/Survey'
import SurveyWelcome from './Survey/SurveyWelcome'
import Treatment from './Treatment/Treatment'
import Session from './Treatment/TreatmentSession'
import VerifyPatient from './VerifyPatient/VerifyPatient'

const useStyles = makeStyles((theme) => ({
  root: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '100%',
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  stepperContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2),
    width: '100%',
    maxWidth: '400px',
  },
  step: {
    borderRadius: theme.spacing(1),
    flex: 1,
    height: '10px',
    background: '#F1F1F1',
  },
  activeStep: {
    background: 'var(--brand-gradient,linear-gradient(118deg,#2badee,#526ceb))',
  },
  completedStep: {
    background: 'var(--brand-gradient,linear-gradient(118deg,#526ceb,#2badee))',
  },
  companyLogoContainer: {
    padding: theme.spacing(3, 0, 6, 0),
  },
  welcomeLogo: {
    height: '110px',
    width: '281px',
  },
  minLogo: {
    height: '71px',
  },
  hidden: {
    display: 'none',
  },
  contentSection: {
    padding: theme.spacing(0, 2, 3, 2),
    width: '100%',
    justifyContent: 'center',
    display: 'flex',
    height: '100%',
    boxSizing: 'border-box',
  },
  noPadding: {
    padding: 0,
  },
  stepper: {
    paddingBottom: theme.spacing(2),
    // borderBottom: '1px solid #DCDBDC',
    // marginBottom: theme.spacing(2),
    // display: 'flex',
    // justifyContent: 'space-between',
    // '& .MuiStepLabel-root': {
    //   flexDirection: 'column-reverse',
    // },
    '& .MuiStep-root': {
      width: 120,
    },
    '& .MuiStepConnector-root': {
      left: 'calc(-50% + 12px)',
      right: 'calc(50% + 12px)',
    },
    '& .MuiStepIcon-text': {
      display: 'none',
    },
    '& .Mui-completed': {
      '& .MuiStepConnector-line': {
        borderColor: '#2badee',
      },
    },
    '& .Mui-active': {
      '& .MuiStepConnector-line': {
        borderColor: '#2badee',
      },
    },
  },
  statusIcon: {},
  pendingStep: {},
  linkLogo: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    alignItems: 'center',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '& img': {
      marginLeft: theme.spacing(1),
    },
    '& span': {
      color: '#182C5A',
      fontWeight: 500,
      fontSize: '12px',
    },
  },
  autoLogo: {
    height: 'auto',
    width: 'auto',
  },
  logoutContainer: {
    marginLeft: 'auto',
    marginRight: theme.spacing(1),
  },
}))

export enum steps {
  'patientType',
  'patientDetails',
  'addCase',
  'addDiagnoses',
  'AddSessions',
  'Survey',
}

const PatientSelfOnboarding = () => {
  const classes = useStyles()
  const { signOut } = useAuth()
  const { pathname } = useLocation()
  const sitesContext = useSitesContext()
  const currentSite = sitesContext.currentSite
  const customLogo = organizationLogo.find(
    (logo) => logo.name === currentSite?.organization.name,
  )
  const isCurrentLocation = (paths: Array<string>) =>
    paths.findIndex((path) => {
      return useRouteMatch(path)
    }, pathname) >= 0

  const onboardingSteps = [
    {
      step: 4,
      key: 'account-treatment',
      path: PatientSelfOnboardUrls.addTreatment.match,
    },
    {
      step: 3,
      key: 'add-diagnoses',
      path: PatientSelfOnboardUrls.addDiagnoses.match,
    },
    {
      step: 2,
      key: 'add-case',
      path: PatientSelfOnboardUrls.addCase.match,
    },
    {
      step: 1,
      key: 'add-patient',
      path: PatientSelfOnboardUrls.newPatient.match,
    },
  ]
  const isSurveyStep = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.survey.match,
  )
  const { patientId, caseId } = isSurveyStep?.params || {}
  const { data: patientCases = [] } = usePatientCases(patientId)
  let manuLogo = undefined
  if (isSurveyStep) {
    const patientCase = findBy('id', caseId, patientCases)
    const treatmentProductManufacturer =
      patientCase?.patientTreatments?.[0]?.treatmentProduct?.manufacturer || ''
    manuLogo = manufacturerLogo.find(
      (logo) =>
        logo.name === treatmentProductManufacturer &&
        treatmentProductManufacturer === currentSite?.organization.name,
    )
  }
  const currentOnboardingStep =
    onboardingSteps.find((step) => useRouteMatch(step.path))?.step || -1

  const isSurveyCompletion = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.surveyCompletion.match,
  )
  const showSignOut = !isSurveyStep && !isSurveyCompletion

  return (
    <>
      <MuiStack
        direction="column"
        // spacing={4}
        className={classes.root}
      >
        <AppBar elevation={0}>
          <Toolbar>
            <a
              className={classes.linkLogo}
              rel="noreferrer"
              href={'/patient-self-onboarding'}
              target="_self"
            >
              <span>POWERED BY </span>
              <img src={brandLogo} height="30" />
            </a>
            {showSignOut && (
              <Box className={classes.logoutContainer}>
                <Button
                  variant="text"
                  size="medium"
                  onClick={() => {
                    signOut()
                  }}
                  startIcon={
                    <Icon>
                      <LogOut />
                    </Icon>
                  }
                >
                  Sign Out
                </Button>
              </Box>
            )}
          </Toolbar>
        </AppBar>
        <Box
          className={clsx(classes.headerSection, {
            [classes.hidden]: isCurrentLocation([
              PatientSelfOnboardUrls.surveyCompletion.match,
            ]),
          })}
        >
          {!manuLogo && customLogo && (
            <Box className={classes.companyLogoContainer}>
              <img
                src={`${process.env.PUBLIC_URL}/assets/orgLogos/${customLogo?.logoImg}`}
                alt={currentSite?.organization.name}
                className={clsx(
                  { [classes.welcomeLogo]: currentOnboardingStep < 0 },
                  { [classes.minLogo]: currentOnboardingStep >= 0 },
                  {
                    [classes.autoLogo]:
                      currentSite?.organization.name === 'EmCyte',
                  },
                )}
              />
            </Box>
          )}
          {manuLogo && (
            <Box className={classes.companyLogoContainer}>
              <img
                src={`${process.env.PUBLIC_URL}/assets/manufacturerLogo/${manuLogo?.logoImg}`}
                alt={currentSite?.organization.name}
                className={clsx(
                  { [classes.welcomeLogo]: currentOnboardingStep < 0 },
                  { [classes.minLogo]: currentOnboardingStep >= 0 },
                  {
                    [classes.autoLogo]:
                      currentSite?.organization.name === 'EmCyte',
                  },
                )}
              />
            </Box>
          )}
          <Box
            className={clsx(classes.stepperContainer, {
              [classes.hidden]: currentOnboardingStep == -1,
            })}
          >
            {onboardingSteps
              .sort((a, b) => a.step - b.step)
              .map((step) => (
                <Box
                  key={step.step}
                  className={clsx(classes.step, {
                    [classes.activeStep]: currentOnboardingStep === step.step,
                    [classes.completedStep]: currentOnboardingStep > step.step,
                  })}
                />
              ))}
          </Box>
        </Box>
        <Box
          className={clsx(classes.contentSection, {
            [classes.noPadding]: isCurrentLocation([
              PatientSelfOnboardUrls.surveyCompletion.match,
            ]),
          })}
        >
          <Switch>
            <Route
              path={PatientSelfOnboardUrls.base.match}
              component={PatientSearch}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.newPatient.match}
              component={Patient}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.existingPatient.match}
              component={VerifyPatient}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.addCase.match}
              component={AddCase}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.addDiagnoses.match}
              component={Diagnosis}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.addTreatment.match}
              component={Treatment}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.existingPatientDetails.match}
              component={PatientDetails}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.addSession.match}
              component={Session}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.survey.match}
              component={SurveyWelcome}
              exact
            />
            <Route
              path={PatientSelfOnboardUrls.surveyCompletion.match}
              component={Survey}
              exact
            />
          </Switch>
        </Box>

        {/* <PatientReport /> */}
      </MuiStack>
    </>
  )
}

export default PatientSelfOnboarding
